import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Tooltip,
  TextField,
} from '@mui/material'
import { Delete as DeleteIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { useUserStore } from '../../stores/userStore'
import { User } from '../../models/User'

export default function UserListPage() {
  const navigate = useNavigate()
  const { users, loading, deleting, creating, error, fetchUsers, deleteUser, createUser } =
    useUserStore()
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState({ email: '', password: '', displayName: '' })
  const [createFormError, setCreateFormError] = useState('')
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const handleDeleteClick = (user: User) => {
    setDeleteTarget(user)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    try {
      await deleteUser(deleteTarget.id)
      setSnackbar({
        open: true,
        message: `已成功刪除用戶「${deleteTarget.displayName}」`,
        severity: 'success',
      })
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || '刪除失敗，請稍後再試',
        severity: 'error',
      })
    } finally {
      setDeleteTarget(null)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteTarget(null)
  }

  const handleCreateOpen = () => {
    setCreateForm({ email: '', password: '', displayName: '' })
    setCreateFormError('')
    setCreateOpen(true)
  }

  const handleCreateConfirm = async () => {
    if (!createForm.email || !createForm.password) {
      setCreateFormError('Email 和密碼為必填')
      return
    }
    setCreateFormError('')
    try {
      await createUser(createForm.email, createForm.password, createForm.displayName || undefined)
      setCreateOpen(false)
      setSnackbar({ open: true, message: '用戶建立成功', severity: 'success' })
    } catch (err: any) {
      setCreateFormError(err?.message || '建立失敗，請稍後再試')
    }
  }

  const columns: GridColDef[] = [
    { field: 'displayName', headerName: '姓名', width: 120 },
    { field: 'email', headerName: 'Email', width: 200 },
    {
      field: 'gender',
      headerName: '性別',
      width: 80,
      valueGetter: (value) => {
        const map: Record<string, string> = {
          male: '男',
          female: '女',
          other: '其他',
        }
        return map[value] || value
      },
    },
    {
      field: 'education',
      headerName: '學歷',
      width: 80,
      valueGetter: (value) => {
        const map: Record<string, string> = {
          bachelor: '學士',
          master: '碩士',
          phd: '博士',
        }
        return map[value] || value
      },
    },
    {
      field: 'grade',
      headerName: '年級',
      width: 80,
      valueGetter: (value) => `${value} 年級`,
    },
    {
      field: 'loginCount',
      headerName: '登入次數',
      width: 100,
      type: 'number',
    },
    {
      field: 'totalScreenTime',
      headerName: '使用時間',
      width: 120,
      valueGetter: (value: number) => {
        if (value < 60) return `${value} 秒`
        if (value < 3600) return `${Math.floor(value / 60)} 分鐘`
        return `${Math.floor(value / 3600)} 小時`
      },
    },
    {
      field: 'lastLoginAt',
      headerName: '最後登入',
      width: 160,
      valueGetter: (value) =>
        value ? format(value, 'yyyy/MM/dd HH:mm', { locale: zhTW }) : '-',
    },
    {
      field: 'registeredAt',
      headerName: '註冊時間',
      width: 160,
      valueGetter: (value) =>
        format(value, 'yyyy/MM/dd HH:mm', { locale: zhTW }),
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title="刪除用戶">
          <IconButton
            size="small"
            color="error"
            onClick={(e) => { e.stopPropagation(); handleDeleteClick(params.row as User); }}
            disabled={deleting}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          用戶管理
        </Typography>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={handleCreateOpen}>
          新增用戶
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={users}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
          onRowClick={(params) => navigate(`/users/${params.row.id}`)}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f0f0f0',
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Card>

      {/* 新增用戶對話框 */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>新增用戶</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Email"
            type="email"
            value={createForm.email}
            onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="密碼"
            type="password"
            value={createForm.password}
            onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
            required
            fullWidth
            helperText="至少 6 個字元"
          />
          <TextField
            label="顯示名稱（選填）"
            value={createForm.displayName}
            onChange={(e) => setCreateForm((f) => ({ ...f, displayName: e.target.value }))}
            fullWidth
            helperText="留空則使用 Email 前綴"
          />
          {createFormError && <Alert severity="error">{createFormError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>取消</Button>
          <Button
            onClick={handleCreateConfirm}
            variant="contained"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} /> : undefined}
          >
            {creating ? '建立中...' : '建立'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 刪除確認對話框 */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel}>
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            確定要刪除用戶「{deleteTarget?.displayName}」（
            {deleteTarget?.email || '無 Email'}）？
            <br />
            <br />
            <strong>此操作無法復原。</strong>
            將會刪除該用戶的帳號、個人資料及所有問卷紀錄。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            取消
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {deleting ? '刪除中...' : '確定刪除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 結果通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
